import React, { CSSProperties, useCallback, useMemo, useState } from 'react'
import { Link } from '@overdose/components'
import { IconHeart, IconPrinter, IconShoppingCart } from '@tabler/icons-react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  useAlgoliaSearchInsignts,
  useCart,
  useCartDrawer,
  useCustomer,
  useWishlist,
} from '~/hooks'
import { UpdateCartRequest } from '~/types'
import { GTM, GTMEvent } from '~/lib'
import { AccountQuickViewDrawer } from '../AccountQuickViewDrawer'
import { Button } from '../Button'
import styles from './MaterialsList.module.css'
import { MaterialsListProps, ProjectMaterial } from './MaterialsList.types'

const MaterialsList = ({
  id,
  name,
  sectionTitle,
  materials,
  children,
  className,
}: MaterialsListProps) => {
  const productToSave = useMemo(() => {
    return materials.map((material: ProjectMaterial) => {
      return { sku: material.product.sku, quantity: material.quantity }
    })
  }, [materials])

  const router = useRouter()
  const { customer } = useCustomer()
  const { addToCart } = useCart()
  const { openCartDrawer } = useCartDrawer()
  const { sendAddToCartEvent } = useAlgoliaSearchInsignts()
  const { createList } = useWishlist(productToSave)

  const [openAccountQuickViewer, setOpenAccountQuickViewer] = useState(false)

  const handleAccountViewer = useCallback(() => {
    if (customer) {
      setOpenAccountQuickViewer(true)
    } else {
      router.push({ ...router, hash: 'login' })
    }
  }, [customer, router])

  const onClickAddAllToCart = useCallback(async () => {
    const skusAddedToCart: string[] = []
    const payload: UpdateCartRequest[] = materials.map(
      (material: ProjectMaterial) => {
        skusAddedToCart.push(material.product.sku)
        return {
          sku: material.product.sku,
          quantity: material.quantity,
        }
      }
    )

    return addToCart.mutate(payload, {
      onSuccess(data) {
        GTM.dispatch(GTMEvent.CLEAR_ECOMMERCE)
        GTM.dispatch(GTMEvent.CART_CONTENTS_PUSH, data)
        sendAddToCartEvent(skusAddedToCart, data.lineItems, data.totalPrice)
        openCartDrawer()
      },
    })
  }, [addToCart, materials, openCartDrawer, sendAddToCartEvent])

  const onClickSaveToList = useCallback(() => {
    if (!name) {
      return
    }

    const productIds: string[] = materials.map((material: ProjectMaterial) => {
      return material.product.sku
    })

    if (productIds.length) {
      createList(name)
    }
  }, [createList, name, materials])

  const onClickPrintList = useCallback(() => {
    window.print()
  }, [])

  const buttonStyles = () => {
    const disabledTextColor = 'var(--greyscale-600, #757575)'
    const disabledBackgroundColor = 'var(--greyscale-300, #e0e0e0)'
    return materials.length === 0
      ? ({
          '--btn-icon-size': '18px',
          '--btn-solid-color-text-primary': disabledTextColor,
          '--btn-solid-color-bg-primary': disabledBackgroundColor,
          '--btn-solid-color-border-primary-hover': disabledBackgroundColor,
          '--btn-solid-color-text-primary-hover': disabledTextColor,
          '--btn-solid-color-bg-primary-hover': disabledBackgroundColor,
          '--btn-solid-color-text-secondary': disabledTextColor,
          '--btn-solid-color-bg-secondary': disabledBackgroundColor,
          '--btn-solid-color-border-secondary-hover': disabledBackgroundColor,
          '--btn-solid-color-text-secondary-hover': disabledTextColor,
          '--btn-solid-color-bg-secondary-hover': disabledBackgroundColor,
        } as CSSProperties)
      : ({
          '--btn-icon-size': '18px',
        } as CSSProperties)
  }

  const materialsListButtonClassNames = classNames(
    'text-base py-2 px-3 normal-case'
  )

  return (
    <div
      id={id}
      className={classNames(
        styles.root,
        styles.wrapper,
        'w-full flex flex-col',
        className
      )}>
      {sectionTitle && (
        <Typography
          className={classNames('mb-4')}
          tag={TypographyTag.p}
          variant={TypographyVariant.BodyXLargeBold}>
          {sectionTitle}
        </Typography>
      )}

      {materials.length > 0 ? (
        <table
          className={classNames('rounded-md', 'table-auto', 'overflow-hidden')}>
          <tbody>
            {materials.map((material, key) => {
              return (
                <tr key={key}>
                  <td>
                    <Typography
                      tag={TypographyTag.span}
                      variant={TypographyVariant.BodyRegularBold}>
                      {material.quantity}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      tag={TypographyTag.span}
                      variant={TypographyVariant.BodyRegular}>
                      {material.product?.title}
                    </Typography>
                  </td>
                  <td className={classNames('text-right')}>
                    <Link
                      to={material.product?.url}
                      title={`View ${material.product?.title} details`}
                      className={classNames('underline', 'text-inherit')}>
                      <Typography
                        tag={TypographyTag.span}
                        variant={TypographyVariant.BodyRegular}>
                        {material.product?.sku}
                      </Typography>
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        <div>{children}</div>
      )}
      <div
        className={classNames(
          'mt-6 flex items-center justify-center gap-2 md:gap-4 flex-wrap'
        )}>
        <div className={classNames('w-full md:w-auto')}>
          <Button
            className={materialsListButtonClassNames}
            disabled={materials.length === 0}
            onClick={onClickAddAllToCart}
            icon={<IconShoppingCart />}
            fluid
            iconAlignment='right'
            size='sm'
            shape='square'
            style={buttonStyles()}>
            Add all items to cart
          </Button>
        </div>
        <div className={classNames('w-full md:w-auto flex gap-2')}>
          {!!name && (
            <Button
              className={materialsListButtonClassNames}
              disabled={materials.length === 0}
              onClick={!customer ? handleAccountViewer : onClickSaveToList}
              icon={<IconHeart />}
              iconAlignment='right'
              fluid
              size='sm'
              status='secondary'
              shape='square'
              style={buttonStyles()}>
              Save to list
            </Button>
          )}
          <Button
            className={materialsListButtonClassNames}
            disabled={materials.length === 0}
            onClick={onClickPrintList}
            icon={<IconPrinter />}
            iconAlignment='right'
            fluid
            size='sm'
            status='secondary'
            shape='square'
            style={buttonStyles()}>
            Print list
          </Button>
        </div>
      </div>
      <AccountQuickViewDrawer
        open={openAccountQuickViewer}
        onClose={() => {
          setOpenAccountQuickViewer(false)
        }}
      />
    </div>
  )
}

export default MaterialsList
